/**
 * HRAttendanceToday — TASK 24c. ภาพรวมการเข้างานวันนี้สำหรับ HR/Manager/Owner.
 *
 * 4 stat cards (ในกะ / สแกนแล้ว / ลา / ยังไม่สแกน), by-shift breakdown,
 * แล้ว 3 tabs (ยังไม่สแกน / สแกนแล้ว / ลา). ในหมวด "ยังไม่สแกน" ทุก row
 * มีปุ่มแจ้งเตือนผ่าน LINE — กดได้ครั้งละคน หรือ bulk ทั้งหมดในคลิกเดียว
 * (throttle 15 นาที + quiet hours guard อยู่ฝั่ง backend).
 */
import { useState } from 'react';
import { AlertTriangle, MapPin } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useAttendanceToday, useNudge, useOrgDepartments, type UnscannedRow, type ScannedRow, type OnLeaveRow } from '../../hooks';
import { useToast } from '../../components/ui/toast';
import { useConfirm } from '../../components/ui/confirm';
import { resolveFileUrl } from '../../lib/api-client';

type Tab = 'unscanned' | 'scanned' | 'on_leave';

function formatRelative(iso: string | null): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'เพิ่งกด';
  if (m < 60) return `${m} นาทีที่แล้ว`;
  const h = Math.floor(m / 60);
  return `${h} ชม.ที่แล้ว`;
}

function todayBkk(): string {
  const bkk = new Date(Date.now() + 7 * 3600_000);
  return bkk.toISOString().slice(0, 10);
}

/** Content-only version used by the Time hub. */
export function HRAttendanceTodayContent() {
  const [date, setDate] = useState(todayBkk);
  const [tab, setTab] = useState<Tab>('unscanned');
  const [deptFilter, setDeptFilter] = useState('');
  const { data, isLoading, refetch } = useAttendanceToday(date);
  const { data: departments } = useOrgDepartments();
  const nudge = useNudge();
  const toast = useToast();
  const confirm = useConfirm();

  function matchDept(row: { department: string | null }): boolean {
    return !deptFilter || row.department === deptFilter;
  }
  const unscannedList = (data?.unscanned_list ?? []).filter(matchDept);
  const scannedList = (data?.scanned_list ?? []).filter(matchDept);
  const onLeaveList = (data?.on_leave_list ?? []).filter(matchDept);

  async function handleNudge(empCodes: string[]) {
    if (empCodes.length === 0) return;
    const label = empCodes.length === 1 ? `แจ้งเตือน ${empCodes[0]}?` : `แจ้งเตือน ${empCodes.length} คนพร้อมกัน?`;
    const ok = await confirm({
      title: label,
      message: 'จะส่ง Flex message ผ่าน LINE ให้พนักงานเปิด LIFF ไปสแกน',
      confirmText: 'ส่งเลย',
    });
    if (!ok) return;
    try {
      const res = await nudge.mutateAsync({ emp_codes: empCodes, date });
      if (res.sent.length > 0) toast.success(`ส่งแล้ว ${res.sent.length} คน`);
      if (res.throttled.length > 0) {
        const reasons = new Set(res.throttled.map((t) => t.reason));
        if (reasons.has('quiet_hours')) toast.error('อยู่ในช่วงห้ามรบกวน (quiet hours)');
        else if (reasons.has('throttle')) toast.success(`ข้าม ${res.throttled.length} คน (เคยเตือนใน 15 นาทีล่าสุด)`);
        else toast.error(`ข้าม ${res.throttled.length} คน (ไม่มี LINE id)`);
      }
      if (res.failed.length > 0) toast.error(`พลาด ${res.failed.length} คน`);
      refetch();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'แจ้งเตือนไม่สำเร็จ');
    }
  }

  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          max={todayBkk()}
          className="input text-sm"
        />
        <button onClick={() => refetch()} className="btn-secondary text-sm">
          รีเฟรช
        </button>
      </div>

      {/* 4 stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 mb-3">
        <div className="card p-3">
          <p className="text-xs text-gray-500">ในกะวันนี้</p>
          <p className="text-2xl font-bold">{data?.summary.total_in_shift ?? '-'}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500">สแกนแล้ว</p>
          <p className="text-2xl font-bold text-emerald-700">{data?.summary.scanned_morning_in ?? '-'}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500">ลา</p>
          <p className="text-2xl font-bold text-blue-700">{data?.summary.on_leave ?? '-'}</p>
        </div>
        <div className="card p-3">
          <p className="text-xs text-gray-500">ยังไม่สแกน</p>
          <p className="text-2xl font-bold text-amber-700">{data?.summary.unscanned ?? '-'}</p>
        </div>
      </div>

      {/* by-shift breakdown */}
      {data && data.by_shift.length > 0 && (
        <div className="card p-3 mb-3">
          <h3 className="font-medium text-sm mb-2">แยกตามกะ</h3>
          <div className="space-y-1">
            {data.by_shift.map((s) => (
              <div key={s.shift_code} className="flex justify-between text-sm">
                <span>{s.shift_label} ({s.start_time}-{s.end_time})</span>
                <span className="text-gray-600">
                  <span className="text-emerald-700 font-medium">{s.scanned}</span>/{s.in_shift}
                  {s.on_leave > 0 && <span className="text-blue-600"> · ลา {s.on_leave}</span>}
                  {s.unscanned > 0 && <span className="text-amber-700"> · ค้าง {s.unscanned}</span>}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter by department */}
      {departments && departments.length > 0 && (
        <div className="mb-3">
          <select
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
            className="input text-sm"
          >
            <option value="">ทุกแผนก</option>
            {departments.map((d) => (
              <option key={d.id} value={d.name}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-3 overflow-x-auto">
        {([
          { id: 'unscanned', label: `ยังไม่สแกน (${data?.summary.unscanned ?? 0})` },
          { id: 'scanned', label: `สแกนแล้ว (${data?.summary.scanned_morning_in ?? 0})` },
          { id: 'on_leave', label: `ลา (${data?.summary.on_leave ?? 0})` },
        ] as const).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap ${
              tab === t.id ? 'bg-brand-600 text-white' : 'bg-white border border-gray-200 text-gray-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-center text-gray-400 py-8 text-sm">กำลังโหลด…</p>}

      {tab === 'unscanned' && data && (
        <>
          {data.unscanned_list.filter((r) => r.can_nudge_now).length > 1 && (
            <button
              onClick={() =>
                handleNudge(data.unscanned_list.filter((r) => r.can_nudge_now).map((r) => r.emp_code))
              }
              disabled={nudge.isPending}
              className="btn-primary w-full mb-3 text-sm"
            >
              📨 แจ้งเตือนทุกคนที่ยังไม่สแกน ({data.unscanned_list.filter((r) => r.can_nudge_now).length})
            </button>
          )}
          {data.unscanned_list.length === 0 ? (
            <div className="card p-6 text-center text-gray-400">
              <p className="text-4xl mb-2">🎉</p>
              <p className="text-sm">ทุกคนสแกนเข้างานครบแล้ว</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.unscanned_list.map((row) => (
                <UnscannedCard key={row.emp_code} row={row} onNudge={() => handleNudge([row.emp_code])} />
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'scanned' && data && (
        <div className="space-y-2">
          {data.scanned_list.length === 0 ? (
            <p className="text-center text-gray-400 py-8 text-sm">ยังไม่มีใครสแกนวันนี้</p>
          ) : (
            data.scanned_list.map((row) => <ScannedCard key={row.emp_code} row={row} />)
          )}
        </div>
      )}

      {tab === 'on_leave' && data && (
        <div className="space-y-2">
          {data.on_leave_list.length === 0 ? (
            <p className="text-center text-gray-400 py-8 text-sm">ไม่มีคนลาวันนี้</p>
          ) : (
            data.on_leave_list.map((row) => <OnLeaveCard key={row.emp_code} row={row} />)
          )}
        </div>
      )}
    </>
  );
}

/** Standalone page kept for legacy /hr/attendance-today (now redirects via Time hub). */
export function HRAttendanceToday() {
  return (
    <AdminLayout title="การเข้างานวันนี้">
      <HRAttendanceTodayContent />
    </AdminLayout>
  );
}

function UnscannedCard({ row, onNudge }: { row: UnscannedRow; onNudge: () => void }) {
  return (
    <div className="card p-3 flex items-center justify-between">
      <div className="min-w-0">
        <p className="font-medium text-sm truncate">
          {row.first_name} {row.last_name}
          {row.nickname && <span className="text-gray-500"> ({row.nickname})</span>}
        </p>
        <p className="text-xs text-gray-500">
          {row.department ?? '-'} · {row.shift_code ?? '-'} ({row.shift_start ?? '-'})
        </p>
        {row.minutes_late > 0 && <p className="text-xs text-amber-700">เลย {row.minutes_late} นาที</p>}
        {row.last_nudged_at && (
          <p className="text-xs text-gray-400">เตือนล่าสุด {formatRelative(row.last_nudged_at)}</p>
        )}
      </div>
      <button
        disabled={!row.can_nudge_now}
        onClick={onNudge}
        className={`text-xs px-3 py-1.5 rounded-lg whitespace-nowrap ml-2 shrink-0 ${
          row.can_nudge_now ? 'bg-brand-600 text-white' : 'bg-gray-200 text-gray-500'
        }`}
      >
        {row.can_nudge_now ? '📨 เตือน' : '⏳ รอ 15 น.'}
      </button>
    </div>
  );
}

function ScannedCard({ row }: { row: ScannedRow }) {
  const [expanded, setExpanded] = useState(false);
  const t = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Bangkok',
  }).format(new Date(row.morning_in_at));
  const hasPic = !!row.selfie_r2_path;
  return (
    <div className="card p-3">
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => hasPic && setExpanded((v) => !v)}
          disabled={!hasPic}
          className="text-left flex-1 min-w-0"
        >
          <p className="font-medium text-sm">
            {row.first_name} {row.last_name}
            {row.nickname && <span className="text-gray-500"> ({row.nickname})</span>}
            {hasPic && <span className="text-gray-400 ml-1 text-xs">📷</span>}
          </p>
          <p className="text-xs text-gray-500">{row.department ?? '-'}</p>
          {row.approval_status === 'pending_review' && (
            <p className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> รอ HR ตรวจสอบ</p>
          )}
          {!row.geofence_passed && <p className="text-xs text-amber-700 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> นอกพื้นที่</p>}
        </button>
        <div className="text-right shrink-0 ml-2">
          <p className="font-mono text-base font-bold text-emerald-700">{t}</p>
          {row.minutes_late > 0 && <p className="text-xs text-amber-700">+{row.minutes_late} น.</p>}
        </div>
      </div>
      {expanded && hasPic && (
        <div className="mt-2">
          <img
            src={resolveFileUrl(row.selfie_r2_path) ?? ''}
            alt={`selfie ${row.emp_code}`}
            className="w-full max-w-xs rounded-lg border border-gray-200"
            loading="lazy"
          />
        </div>
      )}
    </div>
  );
}

function OnLeaveCard({ row }: { row: OnLeaveRow }) {
  return (
    <div className="card p-3">
      <p className="font-medium text-sm">
        {row.first_name} {row.last_name}
        {row.nickname && <span className="text-gray-500"> ({row.nickname})</span>}
      </p>
      <p className="text-xs text-gray-500">{row.department ?? '-'}</p>
      <p className="text-xs text-blue-700 mt-1">
        ลา {row.leave_type}
        {row.reason ? ` — ${row.reason}` : ''}
      </p>
    </div>
  );
}
